import { useState, useRef } from "react";
import { useSession, signOut } from "@/frontend/auth-client";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/frontend/components/ui/avatar";
import { Separator } from "@/frontend/components/ui/separator";
import { 
  Loader2, 
  Camera, 
  Building2, 
  ChevronRight, 
  Trash2 
} from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/frontend/components/ui/dialog";
import { Field, FieldLabel, FieldGroup } from "@/frontend/components/ui/field";
import { readFileAsBase64 } from "@/frontend/utils/shared";
import {
  useFullOrganizationQuery,
  useActiveMemberQuery,
} from "@/frontend/queries/dashboard/settings";
import {
  useUpdateOrganizationMutation,
  useUploadOrgLogoMutation,
  useDeleteOrganizationMutation,
} from "@/frontend/mutations/dashboard/settings";

export function OrganizationSection() {
  const { data: session } = useSession();
  const [orgName, setOrgName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteOrgDialog, setShowDeleteOrgDialog] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [orgLogoError, setOrgLogoError] = useState<string | null>(null);
  const orgLogoInputRef = useRef<HTMLInputElement>(null);
  const isPlatformAdmin = session?.user?.role === "admin";

  const activeOrgQuery = useFullOrganizationQuery();
  const activeMemberQuery = useActiveMemberQuery(!!session);

  const updateOrgMutation = useUpdateOrganizationMutation(() => setIsEditing(false));
  const uploadOrgLogoMutation = useUploadOrgLogoMutation();

  const deleteOrgMutation = useDeleteOrganizationMutation((result) => {
    setShowDeleteOrgDialog(false);
    setDeleteConfirmName("");
    if (result.signedOut) {
      signOut().finally(() => {
        window.location.href = "/";
      });
    } else {
      window.location.href = "/dashboard/patients";
    }
  });

  const org = activeOrgQuery.data;
  const memberRole = activeMemberQuery.data?.role ?? "";
  const roles = memberRole.split(",").map((r) => r.trim()).filter(Boolean);
  const canEditOrg = isPlatformAdmin || roles.includes("admin") || roles.includes("owner");
  const canDeleteOrg = roles.includes("owner");
  const isPairedWorkspace = !!org?.workspaceGroupId;

  if (activeOrgQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!org) return null;

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h2 className="text-xl font-semibold">Workspace</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your workspace's name, logo, and settings.
        </p>
      </div>

      <Separator />

      {/* Logo row */}
      <div className="flex items-center gap-6">
        <div className="relative shrink-0">
          <Avatar className="size-20 rounded-xl">
            <AvatarImage
              src={org.logo || undefined}
              alt={org.name}
              className="object-cover object-center"
            />
            <AvatarFallback className="rounded-xl bg-muted">
              <Building2 className="size-8 text-muted-foreground" />
            </AvatarFallback>
          </Avatar>
          {canEditOrg && (
            <>
              <button
                onClick={() => orgLogoInputRef.current?.click()}
                disabled={uploadOrgLogoMutation.isPending}
                className="absolute bottom-0 right-0 flex size-7 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {uploadOrgLogoMutation.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Camera className="size-3.5" />
                )}
              </button>
              <input
                ref={orgLogoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file || !file.type.startsWith("image/")) return;
                  setOrgLogoError(null);
                  if (file.size > 2 * 1024 * 1024) {
                    setOrgLogoError("Image must be 2 MB or smaller");
                    e.target.value = "";
                    return;
                  }
                  readFileAsBase64(file).then((base64) =>
                    uploadOrgLogoMutation.mutate({ imageBase64: base64 })
                  );
                  e.target.value = "";
                }}
              />
            </>
          )}
        </div>
        <div>
          <p className="text-sm text-muted-foreground">
            {canEditOrg
              ? "Click the camera icon to upload a workspace logo."
              : "Workspace logo."}
          </p>
          {canEditOrg && (
            <>
              <p className="text-xs text-muted-foreground mt-0.5">JPG, PNG, GIF, WebP or SVG · max 2 MB</p>
              {(orgLogoError || uploadOrgLogoMutation.isError) && (
                <p className="text-sm text-destructive mt-1">
                  {orgLogoError ?? uploadOrgLogoMutation.error?.message}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <Separator />

      {/* Name row */}
      <div className="flex flex-col gap-4 max-w-md">
        <div>
          <p className="text-sm font-medium mb-1">Workspace Name</p>
          <p className="text-xs text-muted-foreground">
            The name of your workspace as it appears across the platform.
          </p>
        </div>
        {isEditing && canEditOrg ? (
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="org-name" className="sr-only">Workspace Name</FieldLabel>
              <Input
                id="org-name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="Workspace name"
                autoFocus
              />
            </Field>
            {updateOrgMutation.isError && (
              <p className="text-sm text-destructive">{updateOrgMutation.error.message}</p>
            )}
            <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => updateOrgMutation.mutate({ data: { name: orgName } })}
                disabled={updateOrgMutation.isPending || !orgName.trim()}
              >
                {updateOrgMutation.isPending ? (
                  <><Loader2 className="animate-spin" data-icon="inline-start" />Saving…</>
                ) : "Save changes"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setOrgName(org.name || ""); setIsEditing(false); }}
              >
                Cancel
              </Button>
            </div>
          </FieldGroup>
        ) : (
          <div className="flex items-center justify-between rounded-lg border px-4 py-3">
            <div>
              <p className="font-medium">{org.name}</p>
              <p className="text-xs text-muted-foreground">Slug: {org.slug}</p>
            </div>
            {canEditOrg && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setOrgName(org.name || ""); setIsEditing(true); }}
              >
                Edit <ChevronRight className="size-4 ml-1" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Danger zone — Delete organization (owner only) */}
      {canDeleteOrg && (
        <>
          <Separator />
          <div className="flex flex-col gap-4">
            <div>
              <p className="text-sm font-medium text-destructive">Danger zone</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Permanently delete this workspace
                {isPairedWorkspace ? ", both its Sandbox and Production sides" : ""}. All members will be
                removed. User accounts are not deleted.
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              className="w-fit"
              onClick={() => setShowDeleteOrgDialog(true)}
            >
              <Trash2 className="size-4 mr-2" />
              Delete workspace
            </Button>
          </div>
        </>
      )}

      {/* Delete organization confirmation dialog */}
      <Dialog
        open={showDeleteOrgDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowDeleteOrgDialog(false);
            setDeleteConfirmName("");
            deleteOrgMutation.reset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete workspace</DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{org?.name}</strong>
              {isPairedWorkspace ? ", both its Sandbox and Production sides," : ""} and remove all members.
              Users themselves will not be deleted. Type the workspace name to confirm.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor="delete-confirm">Type &quot;{org?.name}&quot; to confirm</FieldLabel>
            <Input
              id="delete-confirm"
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              placeholder={org?.name ?? ""}
              className="font-mono"
            />
          </Field>
          {deleteOrgMutation.isError && (
            <p className="text-sm text-destructive">
              {deleteOrgMutation.error instanceof Error
                ? deleteOrgMutation.error.message
                : "Failed to delete workspace"}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeleteOrgDialog(false); setDeleteConfirmName(""); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteOrgMutation.mutate({})}
              disabled={
                deleteOrgMutation.isPending ||
                deleteConfirmName.trim() !== (org?.name ?? "")
              }
            >
              {deleteOrgMutation.isPending ? (
                <><Loader2 className="animate-spin" data-icon="inline-start" />Deleting…</>
              ) : (
                "Delete workspace"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
